// The app after sign-in: the top bar (brand, pages, notifications, language, sign out) and the page below it,
// chosen by the address (#inbox, #contacts, #broadcasts, #templates, #assistant, #users, #ai, #settings, #account).
import { callFn, rpc, sb } from "../sb.ts";
import { logoUrl, type Brand } from "../brand.ts";
import { getLang, setLang, t } from "../i18n.ts";
import { h, mount, toast } from "../ui.ts";
import { inbox } from "./inbox.ts";
import { contacts } from "./contacts.ts";
import { templates } from "./templates.ts";
import { broadcasts } from "./broadcasts.ts";
import { users } from "./users.ts";
import { aiPage } from "./ai.ts";
import { assistant } from "./assistant.ts";
import { account } from "./account.ts";
import { setupWizard } from "./setup.ts";

type Page = { id: string; label: string; show: boolean };
let cleanup: (() => void) | void;
let listening = false;

export function shell(root: HTMLElement, brand: Brand, role: string | null, reroute: () => void) {
  const pages: Page[] = [
    { id: "inbox", label: t("Inbox"), show: !!role },
    { id: "contacts", label: t("Contacts"), show: !!role },
    { id: "broadcasts", label: t("Broadcasts"), show: role === "admin" || role === "agent" },
    { id: "templates", label: t("Templates"), show: !!role },
    { id: "assistant", label: t("Assistant"), show: role === "admin" || role === "agent" },
    { id: "ai", label: t("AI"), show: role === "admin" || role === "agent" },
    { id: "users", label: t("Users"), show: role === "admin" },
    { id: "settings", label: t("Settings"), show: role === "admin" },
    { id: "account", label: t("My account"), show: true },
  ].filter((p) => p.show);
  const nav = h("nav", { class: "pages" });
  const view = h("div", { class: "view" });
  const bell = h("button", { class: "icon", title: t("Notifications"), onclick: () => togglePush(bell) }, "🔕");
  const logo = logoUrl(brand.logo_path);
  mount(root, h("div", { class: "app" },
    h("header", { class: "bar" }, logo && h("img", { class: "logo sm", src: logo, alt: "" }), h("b", { class: "brand" }, brand.app_name || brand.company_name),
      nav, h("span", { class: "grow" }), role && bell,
      h("button", { class: "icon", title: "Language", onclick: () => { setLang(getLang() === "ar" ? "en" : "ar"); reroute(); } }, getLang() === "ar" ? "EN" : "ع"),
      h("button", { onclick: async () => { await sb.auth.signOut(); reroute(); } }, t("Sign out"))),
    view));
  if (role) pushButton(bell);

  async function show() {
    const id = location.hash.replace(/^#/, "").split("?")[0] || pages[0].id;
    const page = pages.find((p) => p.id === id) ?? pages[0];
    mount(nav, pages.map((p) => h("a", { href: "#" + p.id, class: p.id === page.id ? "on" : "" }, p.label)));
    if (typeof cleanup === "function") cleanup();
    cleanup = undefined;
    mount(view);
    view.className = "view page-" + page.id;
    if (page.id === "inbox") cleanup = await inbox(view, brand, role!);
    else if (page.id === "contacts") await contacts(view, role!);
    else if (page.id === "broadcasts") await broadcasts(view, role!);
    else if (page.id === "templates") await templates(view, role!);
    else if (page.id === "users") await users(view);
    else if (page.id === "assistant") assistant(view);
    else if (page.id === "ai") await aiPage(view, role!);
    else if (page.id === "settings") await setupWizard(view, brand, () => { location.hash = "#inbox"; reroute(); });
    else await account(view, role);
  }
  if (!listening) {
    window.addEventListener("hashchange", () => current?.());
    // a tapped notification while the app is open: show that chat
    navigator.serviceWorker?.addEventListener("message", (ev) => {
      if (ev.data?.type !== "wa-open" || !ev.data.wa) return;
      history.replaceState(null, "", location.pathname + "?wa=" + encodeURIComponent(ev.data.wa) + "#inbox");
      current?.();
    });
    listening = true;
  }
  current = show;
  show();
}
let current: (() => void) | undefined;

// ---- Web Push on this device (iPhone: only after Add to Home Screen) ----
const pushOk = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const unb64u = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4)), (c) => c.charCodeAt(0));
async function currentSub() {
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}
async function pushButton(b: HTMLButtonElement) {
  if (!pushOk()) { b.hidden = true; return; }
  const on = !!(await currentSub()) && Notification.permission === "granted";
  b.textContent = on ? "🔔" : "🔕";
  b.title = on ? t("Notifications on (this device) — tap to turn off") : t("Turn on notifications for new messages");
}
async function togglePush(b: HTMLButtonElement) {
  try {
    const cur = await currentSub();
    if (cur && Notification.permission === "granted") {
      if (!confirm(t("Turn off notifications on this device?"))) return;
      await callFn("wa-send", { mode: "push_off", endpoint: cur.endpoint });
      await cur.unsubscribe();
      toast(t("Notifications off on this device"));
      return pushButton(b);
    }
    if ((await Notification.requestPermission()) !== "granted") return toast(t("Notifications are blocked: allow them in the browser / phone settings"), true);
    const k = await callFn("wa-send", { mode: "push_key" });
    if (!k.key) return toast(k.error ?? "no key", true);
    const reg = await navigator.serviceWorker.ready;
    const sub = cur ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: unb64u(k.key) });
    const j = sub.toJSON();
    await rpc("save_push_subscription", { p_endpoint: sub.endpoint, p_p256dh: j.keys?.p256dh ?? "", p_auth: j.keys?.auth ?? "", p_ua: navigator.userAgent });
    toast(t("Notifications on: new messages reach this device"));
    pushButton(b);
  } catch (e) { toast(t("Notifications") + ": " + (e as Error).message, true); }
}
